
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import ArticleTemplate from "@/components/ArticleTemplate";
import { allArticles } from "@/data/articleData";
import { Navigate } from "react-router-dom";
import ImageOverlay from "@/components/common/ImageOverlay";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { 
  Calculator, 
  Ship, 
  Anchor, 
  DollarSign, 
  CalendarDays, 
  BarChart4, 
  AlertCircle, 
  CheckCircle2, 
  HelpCircle 
} from "lucide-react";

const VenelainaOpasArticlePage = () => {
  // Use a state for the hero image
  const [heroImage, setHeroImage] = useState("/lovable-uploads/5bf745f2-d3ce-4a35-8963-ae226573d447.png");
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Loan calculator states
  const [loanAmount, setLoanAmount] = useState(20000);
  const [loanTerm, setLoanTerm] = useState(5);
  const [interestRate, setInterestRate] = useState(6.5);
  const [downPayment, setDownPayment] = useState(4000);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalCost, setTotalCost] = useState(0);

  // Find the article by slug
  const article = allArticles.find(article => article.slug === "venelainaopas-nain-rahoitat-unelmiesi-veneen");
  
  // Find related articles in the same category
  const relatedArticles = allArticles
    .filter(a => a.category === "Venelainat" && a.id !== article?.id)
    .slice(0, 3);

  useEffect(() => {
    // Calculate the monthly payment and total interest
    const principal = loanAmount - downPayment;
    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = loanTerm * 12;
    
    if (principal > 0 && monthlyRate > 0 && numberOfPayments > 0) {
      const x = Math.pow(1 + monthlyRate, numberOfPayments);
      const monthly = (principal * x * monthlyRate) / (x - 1);
      
      setMonthlyPayment(monthly);
      setTotalInterest(monthly * numberOfPayments - principal);
      setTotalCost(monthly * numberOfPayments + downPayment);
    } else {
      setMonthlyPayment(principal / numberOfPayments || 0);
      setTotalInterest(0);
      setTotalCost(principal + downPayment);
    }
  }, [loanAmount, loanTerm, interestRate, downPayment]);

  if (!article) {
    return <Navigate to="/artikkelit" replace />;
  }

  const handleImageChange = (newImageUrl: string) => {
    console.log("Image updated to:", newImageUrl);
    setHeroImage(newImageUrl);
    setImageLoaded(true);
    toast.success("Artikkelin kuva päivitetty onnistuneesti!");
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' }).format(value);
  };

  return (
    <ArticleTemplate
      title="Venelainaopas – Näin rahoitat unelmiesi veneen"
      description="Veneen hankintaan on tarjolla useita erilaisia rahoitusvaihtoehtoja. Tämä opas auttaa sinua valitsemaan sopivimman ja edullisimman rahoitustavan venehankinnallesi."
      date={article.date}
      readTime={article.readTime}
      category={article.category}
      imageUrl={heroImage}
      relatedArticles={relatedArticles}
      onImageChange={handleImageChange}
      showEditButton={true}
        content={
          <div>
            <div className="my-6 p-5 bg-blue-50 rounded-lg border border-blue-100">
              <h3 className="flex items-center text-lg font-semibold text-blue-800 mb-2">
                <Ship className="h-5 w-5 mr-2 text-blue-600" />
                Venerahoituksen perustiedot
              </h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Rahoitusvaihtoehtoja tarjoavat pankit, rahoitusyhtiöt ja venekauppiaat</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Laina-ajat tyypillisesti 1-10 vuotta</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Korot 4-10% välillä rahoitusmuodosta riippuen</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle2 className="h-5 w-5 mr-2 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Kilpailuttamalla voit säästää tuhansia euroja</span>
                </li>
              </ul>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
              <div className="rounded-xl overflow-hidden shadow-md">
                <img 
                  src="/lovable-uploads/0bf79631-197f-41db-a0d4-b4f3d094920b.png" 
                  alt="Veneilijä nauttimassa vapaudesta veneen keulalla" 
                  className="w-full h-64 object-cover"
                />
                <div className="p-4 bg-blue-50">
                  <h4 className="font-semibold text-blue-700">Veneilyn vapaus</h4>
                  <p className="text-sm text-gray-600">Oikea rahoitus mahdollistaa unelmiesi veneen hankinnan ja ikimuistoiset kokemukset vesillä.</p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden shadow-md">
                <img 
                  src="/lovable-uploads/df7cbe5c-4ffc-4509-814f-ab63b384127a.png" 
                  alt="Moderni moottorivene kiitää vesillä" 
                  className="w-full h-64 object-cover"
                />
                <div className="p-4 bg-blue-50">
                  <h4 className="font-semibold text-blue-700">Laadukas vene</h4>
                  <p className="text-sm text-gray-600">Hyvin hoidettu vene säilyttää arvonsa - kannattaa panostaa laatuun ja lainaehtoihin.</p>
                </div>
              </div>
            </div>

            <h2>Venelainan vaihtoehdot</h2>
            <p>
              Veneen hankintaan on tarjolla useita erilaisia rahoitusvaihtoehtoja. Oikean vaihtoehdon valinta 
              riippuu monista tekijöistä, kuten veneen hinnasta, omasta taloudellisesta tilanteestasi ja siitä, 
              kuinka paljon olet valmis maksamaan korkoja.
            </p>
            
            <h3>Osamaksukauppa</h3>
            <p>
              Osamaksu on yleisin tapa rahoittaa venehankinta. Tässä rahoitustavassa vene toimii lainan 
              vakuutena, mikä helpottaa rahoituksen saamista. Tyypillisesti maksat käsirahan (10-30% veneen 
              hinnasta) ja loput hinnasta maksat kuukausierissä 2-10 vuoden aikana.
            </p>
            <p>
              <strong>Edut:</strong> Helppo saada, vene toimii vakuutena, nopea päätös.
              <br />
              <strong>Haitat:</strong> Korkeampi korko kuin pankkilainassa, vene on rahoitusyhtiön omistuksessa 
              kunnes viimeinen erä on maksettu.
            </p>
            
            <h3>Vakuudellinen venelaina pankista</h3>
            <p>
              Pankkilaina on usein edullisin vaihtoehto veneen hankintaan. Pankki vaatii yleensä vakuuden, 
              joka voi olla esimerkiksi asunto tai muu omaisuus.
            </p>
            <p>
              <strong>Edut:</strong> Alempi korko, vene on heti omistuksessasi, joustavat lainaehdot.
              <br />
              <strong>Haitat:</strong> Vaatii vakuuden, hitaampi päätös, tiukemmat luottokriteerit.
            </p>
            
            <h3>Vakuudeton kulutusluotto</h3>
            <p>
              Jos vene on edullisempi (yleensä alle 30 000 €), vakuudeton kulutusluotto voi olla hyvä vaihtoehto. 
              Et tarvitse vakuutta, mutta korko on yleensä korkeampi kuin vakuudellisessa lainassa.
            </p>
            <p>
              <strong>Edut:</strong> Ei vaadi vakuutta, nopea päätös, vene on heti omistuksessasi.
              <br />
              <strong>Haitat:</strong> Korkeampi korko, pienempi lainamäärä, lyhyempi takaisinmaksuaika.
            </p>
            
            <h3>Jälleenmyyjän tai venevalmistajan rahoitus</h3>
            <p>
              Monet venekauppiaat tarjoavat omia rahoitusratkaisujaan, usein yhteistyössä rahoitusyhtiöiden kanssa. 
              Nämä voivat olla joskus edullisia, erityisesti jos ne sisältävät erikoistarjouksia.
            </p>
            <p>
              <strong>Edut:</strong> Helppo hakea, joskus erikoistarjouksia (kuten 0% korko ensimmäiselle vuodelle).
              <br />
              <strong>Haitat:</strong> Usein kalliimpi kokonaisuutena, rajoittaa neuvotteluvaraa veneen hinnasta.
            </p>

            <div className="my-8 p-6 bg-white rounded-lg border border-gray-200 shadow-md">
              <h3 className="text-xl font-bold text-blue-700 mb-4 flex items-center">
                <Calculator className="h-5 w-5 mr-2" />
                Venelainan laskuri
              </h3>
              
              <Tabs defaultValue="calculator" className="w-full">
                <TabsList className="grid grid-cols-2 mb-4">
                  <TabsTrigger value="calculator">Lainan laskuri</TabsTrigger>
                  <TabsTrigger value="comparison">Rahoitusvertailu</TabsTrigger>
                </TabsList>
                
                <TabsContent value="calculator" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Veneen hinta: {formatCurrency(loanAmount)}
                        </label>
                        <Slider
                          value={[loanAmount]}
                          min={5000}
                          max={300000}
                          step={1000}
                          onValueChange={(value) => setLoanAmount(value[0])}
                          className="my-2"
                        />
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>5 000 €</span>
                          <span>300 000 €</span>
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Käsiraha: {formatCurrency(downPayment)}
                        </label>
                        <Slider
                          value={[downPayment]}
                          min={0}
                          max={loanAmount * 0.5}
                          step={500}
                          onValueChange={(value) => setDownPayment(value[0])}
                          className="my-2"
                        />
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>0 €</span>
                          <span>{formatCurrency(loanAmount * 0.5)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Laina-aika: {loanTerm} vuotta
                        </label>
                        <Slider
                          value={[loanTerm]}
                          min={1}
                          max={10}
                          step={1}
                          onValueChange={(value) => setLoanTerm(value[0])}
                          className="my-2"
                        />
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>1 vuosi</span>
                          <span>10 vuotta</span>
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Korko: {interestRate.toFixed(1)}%
                        </label>
                        <Slider
                          value={[interestRate]}
                          min={2}
                          max={15}
                          step={0.1}
                          onValueChange={(value) => setInterestRate(value[0])}
                          className="my-2"
                        />
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>2%</span>
                          <span>15%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 p-5 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-4">Laskennan tulokset</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-white p-3 rounded-md shadow-sm">
                        <p className="text-sm text-gray-500">Kuukausierä</p>
                        <p className="text-xl font-bold text-blue-700">{formatCurrency(monthlyPayment)}</p>
                      </div>
                      <div className="bg-white p-3 rounded-md shadow-sm">
                        <p className="text-sm text-gray-500">Korot yhteensä</p>
                        <p className="text-xl font-bold text-blue-700">{formatCurrency(totalInterest)}</p>
                      </div>
                      <div className="bg-white p-3 rounded-md shadow-sm">
                        <p className="text-sm text-gray-500">Kokonaiskustannus</p>
                        <p className="text-xl font-bold text-blue-700">{formatCurrency(totalCost)}</p>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="comparison" className="space-y-4">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rahoitustyyppi</th>
                          <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tyypillinen korko</th>
                          <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Edut</th>
                          <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Haitat</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        <tr>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">Pankkilaina</td>
                          <td className="px-4 py-3 text-sm text-gray-500">4-7%</td>
                          <td className="px-4 py-3 text-sm text-gray-500">Edullisin vaihtoehto, joustavat ehdot</td>
                          <td className="px-4 py-3 text-sm text-gray-500">Vaatii vakuuden, pidemmät käsittelyajat</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">Osamaksu</td>
                          <td className="px-4 py-3 text-sm text-gray-500">6-10%</td>
                          <td className="px-4 py-3 text-sm text-gray-500">Helppo saada, nopea päätös</td>
                          <td className="px-4 py-3 text-sm text-gray-500">Korkeampi korko, vene on vakuutena</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">Kulutusluotto</td>
                          <td className="px-4 py-3 text-sm text-gray-500">8-15%</td>
                          <td className="px-4 py-3 text-sm text-gray-500">Ei vaadi vakuutta, nopea prosessi</td>
                          <td className="px-4 py-3 text-sm text-gray-500">Korkea korko, pienemmät lainasummat</td>
                        </tr>
                        <tr>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">Jälleenmyyjän rahoitus</td>
                          <td className="px-4 py-3 text-sm text-gray-500">5-12%</td>
                          <td className="px-4 py-3 text-sm text-gray-500">Helppo hakea, kampanjatarjoukset</td>
                          <td className="px-4 py-3 text-sm text-gray-500">Usein kalliimpi kokonaisuutena</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-100 flex items-start">
                    <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                    <p className="text-sm text-gray-700">
                      Muista että korko on vain yksi tekijä lainan kokonaiskustannuksissa. Huomioi myös erilaiset 
                      käsittelymaksut, tilinhoitomaksut ja muut kulut vertaillessasi lainatarjouksia.
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
              
              <div className="mt-4 text-center">
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.location.href="/kilpailuta"}>
                  Kilpailuta venelaina nyt
                </Button>
              </div>
            </div>
            
            <h2>Lainan kilpailuttaminen kannattaa</h2>
            <p>
              Vertaile aina eri rahoitusvaihtoehtoja ennen päätöksen tekemistä. Kilpailuttamalla lainan voit säästää 
              jopa tuhansia euroja korkokuluissa. Kiinnitä huomiota seuraaviin asioihin:
            </p>
            
            <ul>
              <li>Todellinen vuosikorko (sisältää kaikki kulut)</li>
              <li>Laina-aika ja sen vaikutus kokonaiskustannuksiin</li>
              <li>Lainan joustavuus (voiko maksaa ennenaikaisesti pois)</li>
              <li>Kuukausierän suuruus ja sen sopivuus omaan talouteesi</li>
              <li>Muut ehdot ja rajoitukset</li>
            </ul>
            
            <div className="my-6 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div className="bg-blue-50 p-4 rounded-lg">
                <DollarSign className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                <h4 className="text-lg font-semibold text-blue-700 mb-1">Säästä</h4>
                <p className="text-sm text-gray-600">Kilpailuttamalla venelainan voit säästää jopa 3000 € korkokuluissa.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <CalendarDays className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                <h4 className="text-lg font-semibold text-blue-700 mb-1">Optimoi</h4>
                <p className="text-sm text-gray-600">Valitse itsellesi sopivin takaisinmaksuaika ja kuukausierä.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <BarChart4 className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                <h4 className="text-lg font-semibold text-blue-700 mb-1">Vertaile</h4>
                <p className="text-sm text-gray-600">Vertaa tarjouksia kokonaisuutena, ei vain koron perusteella.</p>
              </div>
            </div>
            
            <h2>Veneen omistamisen muut kustannukset</h2>
            <p>
              Lainan lisäksi veneen omistamiseen liittyy muitakin kustannuksia, jotka on hyvä huomioida rahoitusta 
              suunnitellessa:
            </p>
            
            <ul>
              <li><strong>Vakuutukset:</strong> Kaskovakuutus on käytännössä pakollinen erityisesti rahoitettavalle veneelle</li>
              <li><strong>Telakointi:</strong> Talvisäilytys maksaa satoja tai jopa tuhansia euroja vuodessa</li>
              <li><strong>Venepaikka:</strong> Laituripaikka voi maksaa sadoista euroista ylöspäin sijainnista riippuen</li>
              <li><strong>Polttoaine:</strong> Erityisesti moottoriveneiden käyttökustannuksista merkittävä osa</li>
              <li><strong>Huollot ja varaosat:</strong> Säännölliset huollot varmistavat veneen turvallisuuden ja arvon säilymisen</li>
            </ul>
            
            <div className="my-6 p-5 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="flex items-center text-lg font-semibold text-gray-800 mb-3">
                <HelpCircle className="h-5 w-5 mr-2 text-blue-600" />
                Yleiset kysymykset venelainasta
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-blue-700 mb-1">Mikä on tyypillinen laina-aika venelainalle?</h4>
                  <p className="text-gray-600">Venelainan tyypillinen laina-aika on 2-10 vuotta, riippuen veneen hinnasta ja rahoitusmuodosta. Edullisemmille veneille 2-5 vuotta on yleisin, kun taas kalliimmille voi saada jopa 10 vuoden rahoituksen.</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-blue-700 mb-1">Voinko saada venelainaa ilman vakuuksia?</h4>
                  <p className="text-gray-600">Kyllä, osamaksukaupassa vene itse toimii vakuutena. Lisäksi pienemmille summille (yleensä alle 30 000 €) on saatavilla vakuudettomia kulutusluottoja. Näissä korko on kuitenkin yleensä korkeampi kuin vakuudellisissa lainoissa.</p>
                </div>
                
                <div>
                  <h4 className="font-medium text-blue-700 mb-1">Kannattaako venettä rahoittaa luottokortilla?</h4>
                  <p className="text-gray-600">Luottokortti ei yleensä ole suositeltava tapa rahoittaa venehankintaa, sillä luottokorttien korot ovat huomattavasti korkeammat kuin muiden rahoitusvaihtoehtojen. Pienissä hankinnoissa ja hyvin lyhyellä maksuajalla se voi kuitenkin olla sopiva väliaikaisratkaisu.</p>
                </div>
              </div>
            </div>
            
            <h2>Näin haet venelainaa Lainasampo.fi:n kautta</h2>
            <p>
              Lainasampo.fi tarjoaa helpon tavan kilpailuttaa veneen rahoitus. Näin haet venelainaa palvelumme kautta:
            </p>
            
            <ol>
              <li>Täytä lainahakemus verkossa - vie vain muutaman minuutin</li>
              <li>Saat tarjoukset usealta rahoituslaitokselta</li>
              <li>Vertaile rahoitustarjouksia rauhassa</li>
              <li>Valitse itsellesi sopivin rahoitus</li>
              <li>Allekirjoita sopimus sähköisesti</li>
              <li>Rahat siirtyvät tilillesi nopeimmillaan seuraavana pankkipäivänä</li>
            </ol>
            
            <h3>Venelainan vaatimukset</h3>
            <p>
              Venelainan myöntämisen edellytykset vaihtelevat rahoittajasta riippuen, mutta yleensä vaaditaan:
            </p>
            <ul>
              <li>Säännölliset tulot</li>
              <li>Hyvä luottohistoria</li>
              <li>18 vuoden ikä (joskus 20 tai 21 vuotta)</li>
              <li>Vakuus (riippuen rahoitusmuodosta)</li>
            </ul>
            
            <h2>Yhteenveto</h2>
            <p>
              Veneen hankinta on merkittävä investointi, ja oikean rahoituksen valinta voi säästää tuhansia euroja. 
              Vertaile eri vaihtoehtoja huolellisesti ja huomioi myös veneen omistamisen kokonaiskustannukset.
            </p>
            <p>
              Lainasampo.fi auttaa sinua löytämään edullisimman rahoituksen unelmiesi veneelle. Kilpailuttamalla 
              lainan varmistat, että et maksa liikaa.
            </p>
            
            <div className="mt-8 text-center">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg text-lg" onClick={() => window.location.href="/kilpailuta"}>
                <Anchor className="h-5 w-5 mr-2" />
                Kilpailuta venelainasi nyt
              </Button>
            </div>
          </div>
        }
      />
  );
};

export default VenelainaOpasArticlePage;
